import React from 'react';
import { useTask } from '../../../../hooks/task';
import { Button } from '../../../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../../../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../../../../components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { deleteTask } from '../../../../api/task';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, FormControl, FormField, FormItem } from '../../../../components/ui/form';
import { Input } from '../../../../components/ui/input';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useProjects } from '../../../../hooks/project';
import SelectComponent from '../../../../components/select';
import toastifyInstance from '../../../../utils/toastify';
import { DatePicker } from '../../../../components/ui/date-picker';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent } from '../../../../components/ui/card';

const formSchema = yup.object().shape({
    search: yup.string(),
});

const TaskList: React.FC = () => {
    const { data, isLoading, query, handleSetQuery } = useTask();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { projects } = useProjects();

    const form = useForm<yup.InferType<typeof formSchema>>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            search: '',
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (taskId: string) => deleteTask(taskId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            toastifyInstance.success('Task deleted successfully');
        },
        onError: () => {
            toastifyInstance.error('Failed to delete task');
        }
    });

    const handleDeleteTask = (taskId: string) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            deleteMutation.mutate(taskId);
        }
    };

    const handleSearch = (values: yup.InferType<typeof formSchema>) => {
        handleSetQuery({ ...query, search: values.search || '' });
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <Card className="w-full">
            <CardContent className="p-2 sm:p-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-2 sm:mt-4 mb-2 sm:mb-4 w-full">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSearch)} className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-end">
                            <FormField
                                control={form.control}
                                name="search"
                                render={({ field }) => (
                                    <FormItem className="w-full sm:w-auto">
                                        <FormControl>
                                            <Input placeholder='Search task...' {...field} className="w-full sm:w-[200px]" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <Button type="submit" className="w-full sm:w-auto">Search</Button>
                        </form>
                    </Form>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                        <div className="w-full sm:w-[200px]">
                            <SelectComponent
                                options={[{ label: 'All', value: 'all' }, ...(projects?.map((project) => ({ label: project.name, value: project._id || '' })) || [])]}
                                onChange={(value) => handleSetQuery({ ...query, projectId: value })}
                                placeholder='Select project'
                                defaultValue={query.projectId}
                            />
                        </div>

                        <div>
                            <DatePicker
                                onChange={(value) => {
                                    if (value) {
                                        const date = new Date(value);
                                        date.setDate(date.getDate() + 1);
                                        handleSetQuery({ ...query, startDate: date.toISOString().split('T')[0] });
                                    } else {
                                        handleSetQuery({ ...query, startDate: '' });
                                    }
                                }}
                                label="Start date"
                                date={new Date(new Date(query.startDate).setDate(new Date(query.startDate).getDate()))}
                            />
                        </div>

                        <div>
                            <DatePicker
                                onChange={(value) => {
                                    if (value) {
                                        const date = new Date(value);
                                        handleSetQuery({ ...query, endDate: date.toISOString().split('T')[0] });
                                    } else {
                                        handleSetQuery({ ...query, endDate: '' });
                                    }
                                }}
                                label="End date"
                                date={new Date(query.endDate)}
                                startDate={new Date(query.startDate)}
                            />
                        </div>

                        <Button variant="outline" size="icon" onClick={() => navigate('/tasks/create-task')} className="w-full sm:w-auto">
                            <Plus className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div className="relative w-full rounded-lg border">
                    <div className="overflow-auto">
                        <div className="min-w-[800px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-white z-10">
                                    <TableRow>
                                        <TableHead className="w-[200px]">Name</TableHead>
                                        <TableHead className="w-[300px]">Description</TableHead>
                                        <TableHead className="w-[150px]">Project</TableHead>
                                        <TableHead className="w-[100px]">Status</TableHead>
                                        <TableHead className="w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data?.tasks?.map(({ task, project }) => (
                                        <TableRow key={task._id}>
                                            <TableCell className="max-w-[200px] truncate">{task.name}</TableCell>
                                            <TableCell className="max-w-[300px] truncate">{task.description}</TableCell>
                                            <TableCell className="max-w-[150px] truncate">{project.name}</TableCell>
                                            <TableCell>
                                                <Badge variant={task.isActive ? "default" : "secondary"} className="whitespace-nowrap">
                                                    {task.isActive ? "Active" : "Inactive"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className='bg-white'>
                                                        <DropdownMenuItem
                                                            className='cursor-pointer hover:bg-gray-100'
                                                            onClick={() => navigate(`/tasks/update-task?taskId=${task._id}`)}
                                                        >
                                                            <Pencil className="mr-2 h-4 w-4" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => handleDeleteTask(task._id)}
                                                            className="text-red-600 cursor-pointer hover:bg-gray-100"
                                                        >
                                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0 mt-4">
                    <div className="text-sm sm:text-base">
                        Total Tasks: {data?.total || 0}
                    </div>
                    <div className="flex gap-2 items-center">
                        <Button
                            disabled={query.page === 1}
                            onClick={() => handleSetQuery({ ...query, page: query.page - 1 })}
                            className="text-sm sm:text-base"
                        >
                            Previous
                        </Button>
                        <p className='text-sm text-gray-500 font-semibold mx-3'>{query.page}</p>
                        <Button
                            disabled={query.page === data?.totalPages}
                            onClick={() => handleSetQuery({ ...query, page: query.page + 1 })}
                            className="text-sm sm:text-base"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default TaskList;